import { HD, FULL_HD } from '@bedrock-rdk/constants';
import { fonts } from './customer/default/fonts';
import { FONT_WEIGHT, FONT } from './constants';

/**
 * Implementation of Bedrock Font System
 * @see https://app.abstract.com/projects/162522cb-a770-4688-9ead-06e1fd627b20/branches/master/collections/df769c2c-9d25-4259-9d9d-1651c96ee421
 */

export { FONT_WEIGHT, FONT } from './constants';

export const descriptors = Object.keys(fonts).flatMap(key => fonts[key].descriptors);

const byFontType = reducer =>
  Object.keys(fonts).reduce(
    (result, fontType) => ({
      ...result,
      [fontType]: reducer(fonts[fontType]),
    }),
    {},
  );

/**
 * Generates Lightning-compatible font descriptor
 *
 * @param {number} fontSize size of font, in pixels
 * @param {number} lineHeight line height, in pixels
 * @param {('regular'|'semi-bold'|'bold'|'black')} weight
 * @param {string} fontFace name of the font face that was specified in App.getFonts static method
 * @returns {object}
 */
export const makeFont = (fontSize, lineHeight, weight) =>
  byFontType(({ fontFamily, fontWeightMap }) => ({
    fontSize,
    lineHeight,
    fontFace: fontFamily,
    fontStyle: fontWeightMap[weight],
    // NOTE: it's required for Lightning to vertically position a string rendered onto canvas
    textBaseline: 'bottom',
  }));

export const makeFontTokyo = weight => ({
  [HD]: makeFont(48, 62, weight),
  [FULL_HD]: makeFont(64, 83, weight),
});

export const makeFontOslo = weight => ({
  [HD]: makeFont(40, 52, weight),
  [FULL_HD]: makeFont(48, 62, weight),
});

export const makeFontParis = weight => ({
  [HD]: makeFont(32, 48, weight),
  [FULL_HD]: makeFont(36, 48, weight),
});

export const makeFontKinshasa = weight => ({
  [HD]: makeFont(24, 36, weight),
  [FULL_HD]: makeFont(32, 48, weight),
});

export const makeFontOttawa = weight => ({
  [HD]: makeFont(20, 30, weight),
  [FULL_HD]: makeFont(24, 36, weight),
});

export const makeFontBerlin = weight => ({
  [HD]: makeFont(16, 24, weight),
  [FULL_HD]: makeFont(20, 30, weight),
});

export const makeFontMadrid = weight => ({
  [HD]: makeFont(14, 21, weight),
  [FULL_HD]: makeFont(18, 27, weight),
});

export const makeFontBoston = weight => ({
  [HD]: makeFont(12, 18, weight),
  [FULL_HD]: makeFont(16, 24, weight),
});

export const makeFontMexico = weight => ({
  [HD]: makeFont(10, 15, weight),
  [FULL_HD]: makeFont(14, 21, weight),
});

export const makeFontLyon = weight => ({
  [HD]: makeFont(12, 12, weight),
  [FULL_HD]: makeFont(24, 31, weight),
});

export const FONTS = {
  // Tokyo
  [FONT.TOKYO_4]: makeFontTokyo(FONT_WEIGHT.ID_4),
  // Oslo
  [FONT.OSLO_1]: makeFontOslo(FONT_WEIGHT.ID_1),
  [FONT.OSLO_2]: makeFontOslo(FONT_WEIGHT.ID_2),
  [FONT.OSLO_3]: makeFontOslo(FONT_WEIGHT.ID_3),
  // Paris
  [FONT.PARIS_1]: makeFontParis(FONT_WEIGHT.ID_1),
  [FONT.PARIS_2]: makeFontParis(FONT_WEIGHT.ID_2),
  [FONT.PARIS_3]: makeFontParis(FONT_WEIGHT.ID_3),
  // Kinshasa
  [FONT.KINSHASA_1]: makeFontKinshasa(FONT_WEIGHT.ID_1),
  [FONT.KINSHASA_2]: makeFontKinshasa(FONT_WEIGHT.ID_2),
  [FONT.KINSHASA_3]: makeFontKinshasa(FONT_WEIGHT.ID_3),
  // Ottawa
  [FONT.OTTAWA_1]: makeFontOttawa(FONT_WEIGHT.ID_1),
  [FONT.OTTAWA_2]: makeFontOttawa(FONT_WEIGHT.ID_2),
  [FONT.OTTAWA_3]: makeFontOttawa(FONT_WEIGHT.ID_3),
  // Berlin
  [FONT.BERLIN_1]: makeFontBerlin(FONT_WEIGHT.ID_1),
  [FONT.BERLIN_2]: makeFontBerlin(FONT_WEIGHT.ID_2),
  [FONT.BERLIN_3]: makeFontBerlin(FONT_WEIGHT.ID_3),
  // Madrid
  [FONT.MADRID_1]: makeFontMadrid(FONT_WEIGHT.ID_1),
  [FONT.MADRID_2]: makeFontMadrid(FONT_WEIGHT.ID_2),
  [FONT.MADRID_3]: makeFontMadrid(FONT_WEIGHT.ID_3),
  // Boston
  [FONT.BOSTON_1]: makeFontBoston(FONT_WEIGHT.ID_1),
  [FONT.BOSTON_2]: makeFontBoston(FONT_WEIGHT.ID_2),
  [FONT.BOSTON_3]: makeFontBoston(FONT_WEIGHT.ID_3),
  // Mexico
  [FONT.MEXICO_1]: makeFontMexico(FONT_WEIGHT.ID_1),
  [FONT.MEXICO_2]: makeFontMexico(FONT_WEIGHT.ID_2),
  [FONT.MEXICO_3]: makeFontMexico(FONT_WEIGHT.ID_3),
  // Lyon
  [FONT.LYON_1]: makeFontLyon(FONT_WEIGHT.ID_1),
  [FONT.LYON_2]: makeFontLyon(FONT_WEIGHT.ID_2),
  [FONT.LYON_3]: makeFontLyon(FONT_WEIGHT.ID_3),
};
